import { z } from 'zod'

// What a reference can point at: the vocabulary an annotation may use. Where a kind resolves belongs
// to the reader, which does not exist yet.
export const REF_KINDS = [
  'formTemplate',
  'customField',
  // The whole set of options, named by a system source for the built-in sets and a uuid for a custom
  // one — so resolution branches on shape, though the reference is the same either way.
  'coreDataSet',
  // Scoped by the set it belongs to: an option id alone does not say which table to look in.
  'coreDataOption',
  'conditionGroup',
  'autoFlaggedScoreCategory',
  'question',
  'documentType',
  // A single choice on a custom field, not the field itself: deleting one orphans the answers that
  // picked it while the field survives.
  'customFieldChoice',
  'policy',
] as const

export type RefKind = (typeof REF_KINDS)[number]

// What a reference can sit inside. Deliberately disjoint from RefKind: references between blocks of
// one workflow never leave the document, so they are not stored and `block` is not a kind.
//
// These nest — a question sits on a page, a task inside a block — which is why the walk records the
// whole trail rather than the innermost one.
export type SiteKind = 'block' | 'page' | 'question' | 'task'

// Declared on zod's own metadata interface, which is empty for exactly this purpose. It is what
// makes a mistyped key at any `.meta()` call a compile error rather than an annotation that silently
// never fires.
declare module 'zod/v4/core' {
  interface GlobalMeta {
    refKind?: RefKind
    scopeFrom?: string
    scope?: string
    siteKind?: SiteKind
    refSet?: { kind: RefKind; scope: string }
  }
}

export type ReferenceMeta = z.core.GlobalMeta

/**
 * The qualifier for a kind only unique within a partition. `scopeFrom` reads it from a field of the
 * document — the walk searches outward from the annotated field, nearest wins; `scope` states it
 * outright, for a field that only ever holds members of one set.
 */
type RefOpts = { scopeFrom?: string; scope?: never } | { scope?: string; scopeFrom?: never }

/**
 * Declares the field's value as a reference. Plain string, because some kinds identify by name
 * rather than by row id; for a uuid or an enum, use `refOn`.
 *
 * Do not chain `.openapi()` onto the result: it clones the schema into its own registry and the
 * annotation is lost, with no error and no row. Call it on the underlying schema first, or on a
 * wrapper such as `.optional()`, both of which keep the annotation reachable.
 */
export const ref = <K extends RefKind>(kind: K, opts?: RefOpts) => z.string().meta({ refKind: kind, ...opts })

/** The same declaration for a field `ref()` cannot supply the base type for — an enum, or a uuid. */
export const refOn = <T extends z.ZodType, K extends RefKind>(schema: T, kind: K, opts?: RefOpts): T =>
  schema.meta({ refKind: kind, ...opts })

/**
 * Declares that carrying this field at all implies a dependency on every member of a scope, for the
 * cases where config stores no id: a bankAccounts question needs every bank-account custom field and
 * names none of them. Belongs on the discriminant, the field that says what this is.
 */
export const refSet = <T extends z.ZodLiteral<string>>(schema: T, set: { kind: RefKind; scope: string }): T =>
  schema.meta({ refSet: set })

/**
 * Marks the field holding the id of the site references here sit in — a block, a question.
 *
 * Goes on the id rather than the object because object metadata does not survive `.extend()`,
 * `.merge()` or `.partial()`, and every question in form config is built from those.
 */
export const siteId = <T extends z.ZodType>(schema: T, kind: SiteKind): T => schema.meta({ siteKind: kind })

/** Where a reference sits, as the trail of enclosing sites, outermost first. */
export type Site = { kind: SiteKind; id: string }
